import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Copy, ArrowRight, Mail, Lock, Loader2 } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { trackPurchase } from '../utils/metaPixel';

const PaymentWelcome = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [status, setStatus] = useState('checking');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (orderId) {
      verifyPayment();
    } else {
      setStatus('failed');
      setLoading(false);
    }
  }, [orderId]);

  const verifyPayment = async () => {
    try {
      // Wait 3 seconds before first check
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const statusRes = await axios.get(`/api/payments/status/${orderId}`);
      
      // Accept both COMPLETED and SUCCESS as valid completion states
      if (statusRes.data.success && ['COMPLETED', 'SUCCESS'].includes(statusRes.data.status)) {
        const welcomeRes = await axios.get(`/api/payments/welcome/${orderId}`);
        setPaymentData(welcomeRes.data);
        setStatus('success');
        trackPurchase(welcomeRes.data.amount / 100, 'INR', welcomeRes.data.plan_name);
        setLoading(false);
      } else if (statusRes.data.status === 'PENDING' || !statusRes.data.success) {
        // Retry for PENDING status OR if status check failed (API error)
        // This prevents temporary failures from showing "Payment Failed"
        setRetryCount(prev => prev + 1);
        
        // Max 20 retries (60 seconds total) before giving up
        if (retryCount < 20) {
          setTimeout(verifyPayment, 3000);
          return;
        } else {
          // After 20 retries, show a more helpful message
          setStatus('timeout');
          setLoading(false);
        }
      } else {
        // Only show failed for actual failed payment states (FAILED, CANCELLED, etc.)
        setStatus('failed');
        setLoading(false);
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      // Retry on network errors instead of immediately showing failed
      setRetryCount(prev => prev + 1);
      
      if (retryCount < 20) {
        // Network error - retry after 5 seconds
        setTimeout(verifyPayment, 5000);
        return;
      } else {
        setStatus('failed');
        setLoading(false);
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Login details copied to clipboard",
    });
  };

  if (loading || status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0F2A] via-[#151827] to-[#0A0F2A] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-cyan-400 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-3">Verifying Payment...</h2>
          <p className="text-gray-400">Please wait while we confirm your transaction</p>
          {retryCount > 0 && (
            <p className="text-gray-500 text-sm mt-2">Checking status... ({retryCount}/20)</p>
          )}
        </div>
      </div>
    );
  }

  if (status === 'timeout') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0F2A] via-[#151827] to-[#0A0F2A] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-gradient-to-br from-[#1A1B2E] to-[#151827] border-2 border-yellow-500/50 rounded-3xl p-8 text-center">
          <div className="text-5xl mb-4">⏱️</div>
          <h2 className="text-3xl font-bold text-white mb-3">Payment Verification Pending</h2>
          <p className="text-gray-300 mb-4">We're still confirming your payment with the payment gateway.</p>
          <p className="text-yellow-400 text-sm mb-6">
            If money was deducted, your subscription will be activated within 5-10 minutes.
            Check your email for confirmation.
          </p>
          <button
            onClick={() => {
              setRetryCount(0);
              setStatus('checking');
              setLoading(true);
              verifyPayment();
            }}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-4 rounded-xl mb-3"
          >
            Check Again
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold py-4 rounded-xl"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0F2A] via-[#151827] to-[#0A0F2A] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-gradient-to-br from-[#1A1B2E] to-[#151827] border-2 border-red-500/50 rounded-3xl p-8 text-center">
          <div className="text-5xl mb-4">❌</div>
          <h2 className="text-3xl font-bold text-white mb-3">Payment Failed</h2>
          <p className="text-gray-300 mb-6">Your payment could not be processed</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-4 rounded-xl"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0F2A] via-[#151827] to-[#0A0F2A] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl w-full bg-gradient-to-br from-[#1A1B2E] to-[#151827] border-2 border-cyan-500/50 rounded-3xl p-8 md:p-12 shadow-2xl shadow-cyan-500/20"
      >
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            🎉 Welcome to Astrowins!
          </h1>
          <p className="text-xl text-cyan-400 font-semibold">
            Payment Successful - Your Subscription is Active
          </p>
        </div>

        <div className="bg-black/40 backdrop-blur rounded-2xl p-6 mb-6 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4">📋 Purchase Details</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Plan:</span>
              <span className="text-white font-semibold">{paymentData?.plan_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Amount Paid:</span>
              <span className="text-cyan-400 font-bold text-lg">₹{(paymentData?.amount / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Status:</span>
              <span className="text-green-400 font-semibold">✅ ACTIVE</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-2 border-cyan-500 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-6 h-6 text-cyan-400" />
            <h3 className="text-xl font-bold text-white">Your Login Access</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-400 mb-1">Email (Username)</div>
              <div className="flex items-center gap-2 bg-black/40 rounded-lg px-4 py-3">
                <Mail className="w-5 h-5 text-cyan-400" />
                <span className="text-white font-mono flex-1">{paymentData?.user_email}</span>
                <button
                  onClick={() => copyToClipboard(paymentData?.user_email)}
                  className="text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            {paymentData?.login_credentials?.is_new_user && paymentData?.login_credentials?.temp_password && (
              <div>
                <div className="text-sm text-gray-400 mb-1">Temporary Password</div>
                <div className="flex items-center gap-2 bg-black/40 rounded-lg px-4 py-3">
                  <Lock className="w-5 h-5 text-cyan-400" />
                  <span className="text-white font-mono flex-1">{paymentData.login_credentials.temp_password}</span>
                  <button
                    onClick={() => copyToClipboard(paymentData.login_credentials.temp_password)}
                    className="text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-yellow-400 text-xs mt-2">⚠️ Please change this password after first login</p>
              </div>
            )}

            {!paymentData?.login_credentials?.is_new_user && (
              <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-4">
                <p className="text-yellow-400 text-sm">
                  💡 Use your existing password to login
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-8">
          <div className="flex items-center gap-2 text-blue-400 text-sm">
            <Mail className="w-5 h-5" />
            <span>Login details have been sent to <strong>{paymentData?.user_email}</strong></span>
          </div>
        </div>

        <button
          onClick={() => navigate('/auth')}
          className="w-full bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 text-white font-bold text-lg py-4 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/50"
          data-testid="login-now-btn"
        >
          Login to Dashboard <ArrowRight className="w-5 h-5" />
        </button>

        <p className="text-gray-400 text-center text-sm mt-6">
          Need help? Contact us at <a href="mailto:support@astrowins.in" className="text-cyan-400 hover:underline">support@astrowins.in</a>
        </p>
      </motion.div>
    </div>
  );
};

export default PaymentWelcome;